/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React from 'react';
import {CSSProperties, PureComponent, ReactElement} from 'react';
import {DefaultInputProps} from "../v2/inputController_v2";
import {TextField} from "../../../../index";
import {DVRInputControllerProps} from "../v2/inputController_v2";

interface Props extends DVRInputControllerProps {
    animatedTexts: string[];
    typingTime: number;
    randomise: boolean;
    doBackspace: boolean;
    Input?: ReactElement;
    className?: string;
}

interface State {
    value: string;
}

export default class AnimatedTextField extends PureComponent<Props, State> {
    static defaultProps = {
        ...DefaultInputProps,
        typingTime: 150,
        randomise: true,
        doBackspace: true,
    };

    state: State = {
        value: '',
    };

    private timer: null | number = null;
    private currentWord: string = '';
    private words: string[] = [];
    private isFocused: boolean = false;

    onFocus = (evt: React.FocusEvent) => {
        const {onFocus} = this.props;
        this.isFocused = true;
        this.stopAnimate();
        if (onFocus) onFocus(evt);
    };

    onBlur = (evt: React.FocusEvent) => {
        const {onBlur, value} = this.props;
        this.isFocused = false;
        if(!value?.toString().length) {
            this.animate();
        }
        if (onBlur) onBlur(evt);
    };

    onChangeNative = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const target = evt.target, value = target.value;
        this.onChange(value, evt);
    };

    onChange = (value: string, evt: React.ChangeEvent) => {
        if (value.length) this.stopAnimate();
        else if (!this.isFocused) this.animate();
        this.props.onChange(value, evt);
    };

    getRandomTexts = (): string[] => {
        const texts = this.props.animatedTexts.slice(), randomised: string[] = [];
        while (texts.length > 0)
            randomised.push(texts.splice(Math.round(Math.random() * (texts.length - 1)), 1)[0]);
        return randomised;
    };

    private animate = () => {
        const {randomise, animatedTexts} = this.props;
        if (randomise) this.words = this.getRandomTexts();
        else this.words = animatedTexts.slice();
        this.currentWord = this.words.pop() || '';
        this.doType();
    };

    private stopAnimate = () => {
        this.setState({value: ''});
        if (this.timer) {
            window.clearTimeout(this.timer);
            this.timer = null;
        }
        this.words = [];
        this.currentWord = '';
    };

    private randomiseTimeout = () => {
        const {typingTime} = this.props, variations: number[] = [1, .8, .4];
        return typingTime * variations[Math.round(Math.random() * 2)];
    };

    private doType = () => {
        const {value} = this.state, {doBackspace, randomise, animatedTexts} = this.props;
        let newValue: string, timeout: number = this.randomiseTimeout();
        if (this.currentWord.length === 0) {
            if (value.length > 0) {
                if (doBackspace) {
                    newValue = value.substr(0, value.length - 1);
                    timeout = Math.round(timeout / 2);
                    if (!newValue.length) timeout = 300;
                } else newValue = '';
            } else {
                if (!this.words.length) {
                    if (randomise) this.words = this.getRandomTexts();
                    else this.words = animatedTexts.slice();
                }
                this.currentWord = this.words.pop() as string;
                newValue = value + this.getNextLetter();
                if (!this.currentWord.length) timeout = 300;
            }
        } else {
            newValue = value + this.getNextLetter();
            if (!this.currentWord.length) timeout = 300;
        }
        this.setState({value: newValue});
        this.timer = window.setTimeout(this.doType, timeout);
    };

    private getNextLetter = () => {
        const letter = this.currentWord.substr(0, 1);
        this.currentWord = this.currentWord.substr(1, this.currentWord.length);
        return letter;
    };

    componentDidMount = () => {
        if (!this.props.autoFocus)
            this.animate();
    };

    componentWillUnmount = () => {
        if (this.timer) {
            window.clearTimeout(this.timer);
            this.timer = null;
        }
    };

    render = () => {
        const value = this.props.value?.toString(), {Input} = this.props;
        let textValue: string, style: CSSProperties;
        if (value?.length) {
            textValue = value;
            style = {};
        } else {
            textValue = this.state.value + (this.state.value.length > 1 ? '|' : '');
            style = {color: "gray"};
        }
        if (Input)
            return React.cloneElement(Input,
                {onChange: this.onChangeNative, value: textValue, onFocus: this.onFocus, onBlur: this.onBlur, style});
        return <TextField {...this.props} onChange={this.onChange} onFocus={this.onFocus} onBlur={this.onBlur}
                                value={textValue} inputProps={{style}}/>;
    };
}
